// BRAI-343 — "mở Brse.ai lên khiến máy lag hơn".
//
// The reporter guessed the audio recording was to blame, but that path is cheap:
// the archive recorder only encodes Opus audio and the STT tap runs in an
// AudioWorklet. The expensive part is the *video* half of the screen share.
//
// We only sample the screen once every SCREEN_CAPTURE_INTERVAL_MS (5s), yet an
// unconstrained `getDisplayMedia({ video: true })` leaves the OS compositor
// pushing frames at the display's native resolution and ~30fps for the entire
// meeting. That work happens below the browser — which is why it reads as "the
// whole machine got slower" rather than "the tab is slow" — and `track.enabled
// = false` does not stop it, it only blanks the frames handed downstream.
//
// Asking for ~1fps up front is the fix: it cuts the continuous cost by roughly
// 30x without touching what we actually consume. `max: 2` leaves the UA a little
// room rather than forcing an exact rate it may not be able to hit.
//
// Deliberately no `width`/`height` here. Capping the frame rate already removes
// almost all of the continuous cost, and resolution constraints on display
// capture behave inconsistently across browsers (ignored by some, letterboxed or
// cropped by others) — a distorted screenshot is a worse trade than the sliver
// of CPU it would still save. Frame size is instead reduced on our side, right
// before JPEG encoding, via `fitCaptureSize`.
export const SCREEN_SHARE_VIDEO_CONSTRAINTS: MediaTrackConstraints = {
  frameRate: { ideal: 1, max: 2 },
}

/**
 * Longest edge, in pixels, that a captured frame is scaled down to before it is
 * JPEG-encoded and base64'd onto the STT socket.
 *
 * A full-screen share on a Retina display is ~7.7 megapixels; encoding that
 * every 5s stalls the main thread and produces a payload in the megabytes.
 * 1280px keeps slide and document text legible — it is around what vision models
 * downscale to internally anyway — while cutting both costs by roughly 6x.
 */
export const SCREEN_CAPTURE_MAX_DIMENSION = 1280

export interface CaptureSize {
  width: number
  height: number
}

/**
 * Scales a frame to fit inside a `maxDimension` box, preserving aspect ratio.
 * Never upscales, so sharing a single small window stays pixel-exact.
 *
 * @param width Source frame width, in pixels.
 * @param height Source frame height, in pixels.
 * @param maxDimension Longest edge allowed on the result.
 * @returns Integer dimensions to size the encoding canvas with.
 */
export function fitCaptureSize(
  width: number,
  height: number,
  maxDimension: number = SCREEN_CAPTURE_MAX_DIMENSION
): CaptureSize {
  const longest = Math.max(width, height)
  if (longest <= maxDimension) {
    return { width, height }
  }

  const scale = maxDimension / longest
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  }
}
